/**
 * SnapshotView Page
 *
 * Read-only view of a history log snapshot.
 * Same tabbed layout as the history log but with all editing disabled.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Camera,
  Stethoscope,
  Pill,
  Syringe,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  AlertTriangle,
  Bot,
} from 'lucide-react';
import * as mhrApi from '../lib/api';
import type {
  HistorySnapshot,
  HistoryDiagnosis,
  HistoryMedication,
  HistoryProcedure,
  DiagnosisStatus,
  MedicationStatus,
  ProcedureStatus,
  ConfidenceLevel,
  SNAPSHOT_TYPE_LABELS,
} from '../types';

type TabType = 'diagnoses' | 'medications' | 'procedures';

const TABS: Array<{ id: TabType; label: string; icon: typeof Stethoscope }> = [
  { id: 'diagnoses', label: 'Diagnoses', icon: Stethoscope },
  { id: 'medications', label: 'Medications', icon: Pill },
  { id: 'procedures', label: 'Procedures', icon: Syringe },
];

const SNAPSHOT_TYPES: Record<string, string> = {
  manual: 'Manual',
  pre_review: 'Pre-Review',
  milestone: 'Milestone',
};

const STATUS_LABELS: Record<string, string> = {
  active: 'Active', resolved: 'Resolved', historical: 'Historical',
  current: 'Current', discontinued: 'Discontinued', prn: 'PRN',
  completed: 'Completed', planned: 'Planned', cancelled: 'Cancelled',
  uncertain: 'Uncertain',
};

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  active: { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-300' },
  resolved: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300' },
  historical: { bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-700 dark:text-blue-300' },
  current: { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-300' },
  discontinued: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300' },
  prn: { bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-700 dark:text-amber-300' },
  completed: { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-300' },
  planned: { bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-700 dark:text-blue-300' },
  cancelled: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300' },
  uncertain: { bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-700 dark:text-amber-300' },
};

const CONFIDENCE_COLORS: Record<ConfidenceLevel, { bg: string; text: string }> = {
  high: { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-700 dark:text-green-300' },
  medium: { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-300' },
  low: { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-700 dark:text-red-300' },
};

function formatDate(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return dateStr; }
}

interface CollapsibleSectionProps {
  title: string;
  count: number;
  color: string;
  bgColor: string;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}

function CollapsibleSection({ title, count, color, bgColor, defaultExpanded = true, children }: CollapsibleSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  if (count === 0) return null;
  return (
    <div className="mb-4 last:mb-0">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg ${bgColor} hover:opacity-80 transition-opacity`}
      >
        <div className="flex items-center gap-2">
          {isExpanded ? <ChevronDown size={16} className={color} /> : <ChevronRight size={16} className={color} />}
          <span className={`text-sm font-medium ${color}`}>{title}</span>
        </div>
        <span className={`text-xs font-medium ${color} bg-white/50 dark:bg-white/10 px-2 py-0.5 rounded-full`}>{count}</span>
      </button>
      {isExpanded && <div className="mt-2 space-y-2">{children}</div>}
    </div>
  );
}

function ReadOnlyEntryCard({ entry, entryType }: { entry: any; entryType: string }) {
  const name = entryType === 'diagnosis' ? entry.condition_name
    : entryType === 'medication' ? entry.medication_name
    : entry.procedure_name;

  const statusColor = STATUS_COLORS[entry.status] || STATUS_COLORS.uncertain;
  const confColor = CONFIDENCE_COLORS[entry.confidence as ConfidenceLevel] || CONFIDENCE_COLORS.medium;
  const isVerified = !!entry.verified_by;

  let details = '';
  if (entryType === 'medication') {
    const parts: string[] = [];
    if (entry.dose) parts.push(entry.dose);
    if (entry.frequency) parts.push(entry.frequency);
    if (entry.route) parts.push(entry.route);
    details = parts.join(' | ');
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border ${isVerified ? 'border-green-200 dark:border-green-800' : 'border-gray-200 dark:border-gray-700'} p-3`}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{name}</span>
          {entry.is_ai_generated && (
            <span className="inline-flex items-center gap-0.5 text-xs text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 px-1.5 py-0.5 rounded">
              <Bot size={10} /> AI
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {isVerified && (
            <span className="inline-flex items-center gap-1 text-xs text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 px-1.5 py-0.5 rounded-full">
              <CheckCircle size={10} /> Verified
            </span>
          )}
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColor.bg} ${statusColor.text}`}>
            {STATUS_LABELS[entry.status] || entry.status}
          </span>
        </div>
      </div>
      {details && <p className="text-xs text-gray-500 dark:text-gray-400">{details}</p>}
      <div className="flex items-center gap-2 mt-2">
        <span className={`text-xs px-1.5 py-0.5 rounded ${confColor.bg} ${confColor.text}`}>
          {entry.confidence === 'low' && <AlertTriangle size={10} className="inline mr-0.5" />}
          {entry.confidence}
        </span>
        {entry.source_document_ids?.length > 0 && (
          <span className="text-xs text-gray-400 dark:text-gray-500">From {entry.source_document_ids.length} doc(s)</span>
        )}
      </div>
    </div>
  );
}

export function SnapshotView() {
  const { patientId, snapshotId } = useParams<{ patientId: string; snapshotId: string }>();
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState<HistorySnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('diagnoses');

  useEffect(() => {
    async function load() {
      if (!patientId || !snapshotId) return;
      setLoading(true);
      try {
        // Get the patient chart to find the history log ID
        const chart = await mhrApi.getPatientChart(patientId);
        if (!chart.history_log) {
          setError('No history log found');
          return;
        }
        const snap = await mhrApi.getSnapshot(chart.history_log.id, snapshotId);
        setSnapshot(snap);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load snapshot');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [patientId, snapshotId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !snapshot) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">{error || 'Snapshot not found'}</h3>
        <button onClick={() => navigate(-1)} className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300">
          Go Back
        </button>
      </div>
    );
  }

  const diagnoses = (snapshot.diagnoses_snapshot || []) as HistoryDiagnosis[];
  const medications = (snapshot.medications_snapshot || []) as HistoryMedication[];
  const procedures = (snapshot.procedures_snapshot || []) as HistoryProcedure[];

  const tabCounts: Record<TabType, number> = {
    diagnoses: diagnoses.length,
    medications: medications.length,
    procedures: procedures.length,
  };

  const groupByStatus = <T extends { status: string }>(items: T[], statuses: string[]): Record<string, T[]> => {
    return statuses.reduce((acc, s) => {
      acc[s] = items.filter(i => i.status === s);
      return acc;
    }, {} as Record<string, T[]>);
  };

  const dxGroups = groupByStatus(diagnoses, ['active', 'resolved', 'historical', 'uncertain']);
  const medGroups = groupByStatus(medications, ['current', 'discontinued', 'historical', 'prn', 'uncertain']);
  const procGroups = groupByStatus(procedures, ['completed', 'planned', 'cancelled', 'uncertain']);

  const sectionColors: Record<string, { color: string; bgColor: string }> = {
    active: { color: 'text-green-700 dark:text-green-300', bgColor: 'bg-green-50 dark:bg-green-900/20' },
    resolved: { color: 'text-gray-700 dark:text-gray-300', bgColor: 'bg-gray-50 dark:bg-gray-900/40' },
    historical: { color: 'text-blue-700 dark:text-blue-300', bgColor: 'bg-blue-50 dark:bg-blue-900/20' },
    uncertain: { color: 'text-amber-700 dark:text-amber-300', bgColor: 'bg-amber-50 dark:bg-amber-900/20' },
    current: { color: 'text-green-700 dark:text-green-300', bgColor: 'bg-green-50 dark:bg-green-900/20' },
    discontinued: { color: 'text-gray-700 dark:text-gray-300', bgColor: 'bg-gray-50 dark:bg-gray-900/40' },
    prn: { color: 'text-amber-700 dark:text-amber-300', bgColor: 'bg-amber-50 dark:bg-amber-900/20' },
    completed: { color: 'text-green-700 dark:text-green-300', bgColor: 'bg-green-50 dark:bg-green-900/20' },
    planned: { color: 'text-blue-700 dark:text-blue-300', bgColor: 'bg-blue-50 dark:bg-blue-900/20' },
    cancelled: { color: 'text-gray-700 dark:text-gray-300', bgColor: 'bg-gray-50 dark:bg-gray-900/40' },
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg mt-1"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <Camera size={20} className="text-primary-600 dark:text-primary-400" />
            <h1 className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">{snapshot.snapshot_name}</h1>
            <span className="text-xs font-medium px-2 py-1 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-400">
              {SNAPSHOT_TYPES[snapshot.snapshot_type] || snapshot.snapshot_type}
            </span>
            <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
              Read Only
            </span>
          </div>
          {snapshot.description && <p className="text-gray-600 dark:text-gray-400 mt-1">{snapshot.description}</p>}
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            <Clock size={14} className="inline mr-1" />
            Captured {formatDate(snapshot.created_at)}
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
          <Stethoscope size={16} className="text-gray-400 dark:text-gray-500" />
          <span>{snapshot.diagnosis_count} diagnoses</span>
        </div>
        <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
          <Pill size={16} className="text-gray-400 dark:text-gray-500" />
          <span>{snapshot.medication_count} medications</span>
        </div>
        <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
          <Syringe size={16} className="text-gray-400 dark:text-gray-500" />
          <span>{snapshot.procedure_count} procedures</span>
        </div>
        {snapshot.unresolved_discrepancy_count > 0 && (
          <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
            <AlertTriangle size={16} />
            <span>{snapshot.unresolved_discrepancy_count} discrepancies at time of snapshot</span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors duration-150 ${
                  isActive
                    ? 'border-primary-600 text-primary-700 dark:text-primary-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <Icon size={18} />
                {tab.label}
                <span className={`text-xs px-2 py-0.5 rounded-full ${isActive ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`}>
                  {tabCounts[tab.id]}
                </span>
              </button>
            );
          })}
        </div>

        <div className="p-4">
          {activeTab === 'diagnoses' && (
            diagnoses.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">No diagnoses in this snapshot</p>
            ) : (
              Object.entries(dxGroups).map(([status, items]) => {
                const sc = sectionColors[status] || sectionColors.uncertain;
                return (
                  <CollapsibleSection key={status} title={STATUS_LABELS[status] || status} count={items.length} color={sc.color} bgColor={sc.bgColor}>
                    {items.map(d => <ReadOnlyEntryCard key={d.id} entry={d} entryType="diagnosis" />)}
                  </CollapsibleSection>
                );
              })
            )
          )}
          {activeTab === 'medications' && (
            medications.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">No medications in this snapshot</p>
            ) : (
              Object.entries(medGroups).map(([status, items]) => {
                const sc = sectionColors[status] || sectionColors.uncertain;
                return (
                  <CollapsibleSection key={status} title={STATUS_LABELS[status] || status} count={items.length} color={sc.color} bgColor={sc.bgColor}>
                    {items.map(m => <ReadOnlyEntryCard key={m.id} entry={m} entryType="medication" />)}
                  </CollapsibleSection>
                );
              })
            )
          )}
          {activeTab === 'procedures' && (
            procedures.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">No procedures in this snapshot</p>
            ) : (
              Object.entries(procGroups).map(([status, items]) => {
                const sc = sectionColors[status] || sectionColors.uncertain;
                return (
                  <CollapsibleSection key={status} title={STATUS_LABELS[status] || status} count={items.length} color={sc.color} bgColor={sc.bgColor}>
                    {items.map(p => <ReadOnlyEntryCard key={p.id} entry={p} entryType="procedure" />)}
                  </CollapsibleSection>
                );
              })
            )
          )}
        </div>
      </div>
    </div>
  );
}
